import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClientNavbar from '../components/client/ClientNavbar';
import ClientWelcome from '../components/client/ClientWelcome';
import ClientFilters from '../components/client/ClientFilters';
import ClientTourGrid from '../components/client/ClientTourGrid';
import ClientGDPR from '../components/client/ClientGDPR';
import ClientEditModal from '../components/client/ClientEditModal';
import ClientPasswordModal from '../components/client/ClientPasswordModal';
import ClientCreateModal from '../components/client/ClientCreateModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ClientImageViewer from '../components/client/ClientImageViewer';
import Toast from '../components/Toast';
import Button from '../components/common/Button';
import api from '../api/axios';
import { copyToClipboard } from '../utils/clipboard';

import { useAuth } from '../context/AuthContext';

// Hooks
import { useAgencyData } from '../hooks/useAgencyData';
import { useFilters } from '../hooks/useFilters';
import { useTours } from '../hooks/useTours';
import { useTourForm } from '../hooks/useTourForm';
import { useImageManagement } from '../hooks/useImageManagement';
import { usePasswordManagement } from '../hooks/usePasswordManagement';

export default function ClientDashboard() {
    const { logout, user } = useAuth();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' });

    // Initialize Hooks
    const {
        agency, cities, areas, propertyTypes, systemConfig,
        fetchAgency, fetchCities, fetchAreas, fetchPropertyTypes, fetchSystemConfig
    } = useAgencyData();

    const {
        filters, handleFilterChange, clearFilters, hasActiveFilters
    } = useFilters();

    const {
        tours, setTours, fetchTours,
        isStatusChangeModalOpen, setIsStatusChangeModalOpen,
        statusChangeData, setStatusChangeData,
        isDeleteTourModalOpen, setIsDeleteTourModalOpen,
        tourToDelete, setTourToDelete,
        handleStatusChange, confirmStatusChange,
        handleDeleteTour, confirmDeleteTour
    } = useTours(filters, setToast, t);

    const {
        isCreateModalOpen, setIsCreateModalOpen,
        isEditModalOpen, setIsEditModalOpen,
        uploadProgress, createForm, editForm, editingTour,
        handleCreateClick, handleCreateChange, handleCreateSubmit,
        handleEditClick, handleEditChange, handleEditSubmit,
        handleDeleteImage, handleAddImages
    } = useTourForm(agency, setTours, setToast, t, systemConfig);

    const {
        isImageViewerOpen, currentViewerImages, currentImageIndex,
        openImageViewer, closeImageViewer, nextImage, prevImage
    } = useImageManagement();

    const {
        showPasswordChangeModal, setShowPasswordChangeModal,
        passwordForm, passwordError,
        handlePasswordChange, handlePasswordSubmit
    } = usePasswordManagement(user, setToast, t);

    // Initial Data Fetch
    useEffect(() => {
        if (user) {
            fetchAgency();
            if (user.must_change_password) {
                setShowPasswordChangeModal(true);
            }
        }
        fetchCities();
        fetchPropertyTypes();
        fetchSystemConfig();
    }, [user]);

    // Fetch areas when city changes - Removed, now using config data
    // useEffect(() => {
    //     fetchAreas(filters.city);
    // }, [filters.city]);

    const handleAddSubarea = async (cityEn, arName, enName) => {
        try {
            const response = await api.post(`/locations/cities/${cityEn}/subareas`, {
                ar: arName,
                en: enName
            });
            await fetchCities(); // refresh cities dropdown
            return response.data;
        } catch (err) {
            console.error('Failed to add subarea:', err);
            throw err;
        }
    };

    // Copy Link Handler - uses /tours/{id}/view endpoint for proper OG meta tags
    const copyLink = (id) => {
        const url = `${window.location.origin}/tours/${id}/view`;
        copyToClipboard(url)
            .then(() => {
                setToast({
                    isVisible: true,
                    message: t('linkCopied'),
                    type: 'success'
                });
            })
            .catch((err) => {
                console.error('Copy failed:', err);
                setToast({
                    isVisible: true,
                    message: t('copyFailed') || 'Failed to copy link',
                    type: 'error'
                });
            });
    };

    // GDPR Export Handler
    const handleExportData = async () => {
        try {
            const response = await api.get('/agencies/me/export', {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `agency_data_${new Date().toISOString().split('T')[0]}.json`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setToast({
                isVisible: true,
                message: t('dataExportedSuccessfully'),
                type: 'success'
            });
        } catch (err) {
            console.error('Failed to export data:', err);
            setToast({
                isVisible: true,
                message: t('dataExportFailed'),
                type: 'error'
            });
        }
    };

    // Account Deletion Handler
    const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const REQUIRED_CONFIRMATION = 'DELETE MY ACCOUNT';

    const handleDeleteAccount = async () => {
        setDeleteConfirmText('');
        setIsDeleteAccountModalOpen(true);
    };

    const confirmDeleteAccount = async () => {
        if (deleteConfirmText.trim().toUpperCase() !== REQUIRED_CONFIRMATION) {
            setToast({
                isVisible: true,
                message: t('pleaseTypeConfirmation') || `Please type "${REQUIRED_CONFIRMATION}" to confirm`,
                type: 'error'
            });
            return;
        }

        try {
            await api.delete(`/agencies/me?confirmation_text=${encodeURIComponent(deleteConfirmText)}`);
            logout();
            // Redirect happens automatically via PrivateRoute or App structure
        } catch (err) {
            console.error('Failed to delete account:', err);
            setToast({
                isVisible: true,
                message: err.response?.data?.detail || t('deleteFailed') || "Failed to delete account",
                type: 'error'
            });
            setIsDeleteAccountModalOpen(false);
        }
    };

    if (!agency) return <div>Loading...</div>;

    return (
        <div className="min-h-screen bg-page-gradient">
            <ClientNavbar agency={agency} logout={logout} />

            <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <ClientWelcome agency={agency} />

                <ClientFilters
                    filters={filters}
                    handleFilterChange={handleFilterChange}
                    cities={cities}
                    hasActiveFilters={hasActiveFilters}
                    clearFilters={clearFilters}
                />

                <div className={`mb-6 w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                    <Button
                        onClick={handleCreateClick}
                        variant="primary"
                        className="inline-flex items-center gap-2 shadow-lg bg-gold-gradient text-white border-none hover:opacity-90 hover:scale-[1.02] transform transition-all duration-300"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                        {t('createNewListing') || 'Create New Listing'}
                    </Button>
                </div>

                <ClientTourGrid
                    tours={tours}
                    hasActiveFilters={hasActiveFilters}
                    openImageViewer={openImageViewer}
                    copyLink={copyLink}
                    handleEditClick={handleEditClick}
                    handleStatusChange={handleStatusChange}
                    handleDeleteTour={handleDeleteTour}
                    cities={cities}
                    propertyTypes={propertyTypes}
                />

                <ClientGDPR
                    handleExportData={handleExportData}
                    handleDeleteAccount={handleDeleteAccount}
                />
            </div>

            <ClientEditModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                editForm={editForm}
                editingTour={editingTour}
                handleEditChange={handleEditChange}
                handleEditSubmit={handleEditSubmit}
                propertyTypes={propertyTypes}
                cities={cities}
                areas={areas}
                handleDeleteImage={handleDeleteImage}
                handleAddImages={handleAddImages}
                supportedCurrencies={systemConfig.supportedCurrencies}
                handleAddSubarea={handleAddSubarea}
            />

            <ClientPasswordModal
                isOpen={showPasswordChangeModal}
                onClose={() => {
                    if (!user?.must_change_password) {
                        setShowPasswordChangeModal(false);
                    }
                }}
                passwordForm={passwordForm}
                handlePasswordChange={handlePasswordChange}
                handlePasswordSubmit={handlePasswordSubmit}
                passwordError={passwordError}
                email={user?.email}
                isForced={user?.must_change_password}
            />

            <ClientCreateModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                createForm={createForm}
                handleCreateChange={handleCreateChange}
                handleCreateSubmit={handleCreateSubmit}
                propertyTypes={propertyTypes}
                cities={cities}
                uploadProgress={uploadProgress}
                supportedCurrencies={systemConfig.supportedCurrencies}
                handleAddSubarea={handleAddSubarea}
            />

            <ConfirmationModal
                isOpen={isStatusChangeModalOpen}
                onClose={() => {
                    setIsStatusChangeModalOpen(false);
                    setStatusChangeData({ tourId: null, newStatus: '', message: '', successMessage: '' });
                }}
                onConfirm={confirmStatusChange}
                title={t('confirmAction')}
                message={statusChangeData.message}
                confirmText={t('yes')}
                cancelText={t('cancel')}
                isDanger={statusChangeData.newStatus === 'sold'}
            />

            <ClientImageViewer
                isOpen={isImageViewerOpen}
                onClose={closeImageViewer}
                images={currentViewerImages}
                currentIndex={currentImageIndex}
                nextImage={nextImage}
                prevImage={prevImage}
            />

            <ConfirmationModal
                isOpen={isDeleteTourModalOpen}
                onClose={() => {
                    setIsDeleteTourModalOpen(false);
                    setTourToDelete(null);
                }}
                onConfirm={confirmDeleteTour}
                title={t('deleteTour')}
                message={t('confirmDeleteTour')}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                isDanger={true}
            />

            {/* Delete Account Modal with Confirmation Input */}
            {isDeleteAccountModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity" aria-hidden="true"></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-bg-card border border-gold/20 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 relative z-10">
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-danger-100 sm:mx-0 sm:mr-4 sm:h-10 sm:w-10">
                                    <svg className="h-6 w-6 text-danger-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                                    <h3 className="text-lg leading-6 font-medium text-text" id="modal-title">
                                        {t('deleteAccount')}
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-text-muted mb-4">
                                            {t('deleteAccountWarning') || 'This action will permanently delete your account, all tours, and all listings. This cannot be undone.'}
                                        </p>
                                        <p className="text-sm text-danger-600 font-medium mb-2">
                                            {t('typeToConfirm') || `Type "${REQUIRED_CONFIRMATION}" to confirm:`}
                                        </p>
                                        <input
                                            type="text"
                                            value={deleteConfirmText}
                                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                                            placeholder={REQUIRED_CONFIRMATION}
                                            className="w-full px-3 py-2 border border-gold/20 rounded-md bg-bg-soft text-text focus:outline-none focus:ring-2 focus:ring-danger-500 focus:border-danger-500"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                                <button
                                    type="button"
                                    disabled={deleteConfirmText.trim().toUpperCase() !== REQUIRED_CONFIRMATION}
                                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm transition-colors ${deleteConfirmText.trim().toUpperCase() === REQUIRED_CONFIRMATION
                                        ? 'bg-danger-600 hover:bg-danger-700 focus:ring-danger-500'
                                        : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    onClick={confirmDeleteAccount}
                                >
                                    {t('delete')}
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gold/20 shadow-sm px-4 py-2 bg-bg-soft text-base font-medium text-text-muted hover:bg-gold/10 hover:text-gold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:mt-0 sm:w-auto sm:text-sm transition-colors"
                                    onClick={() => setIsDeleteAccountModalOpen(false)}
                                >
                                    {t('cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.isVisible}
                onClose={() => setToast({ ...toast, isVisible: false })}
                duration={5000}
            />
        </div>
    );
}
